import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api, trackEvent } from '../../shared/utils/api';
import { Check, Warning } from '../../shared/components/Icons';
import { formatDate } from '../../shared/utils/dateUtils';

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ message, type = 'success', onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3500);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className={`sqe-toast sqe-toast--${type}`}>
      {type === 'success' ? <Check size={13} /> : <Warning size={13} />}
      {message}
    </div>
  );
}

function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((message, type = 'success') => setToast({ message, type }), []);
  const el = toast ? <Toast key={toast.message + Date.now()} message={toast.message} type={toast.type} onDone={() => setToast(null)} /> : null;
  return [el, show];
}

// ── Confirmation modal ────────────────────────────────────────────────────────

function ConfirmModal({ heading, body, confirmLabel, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{heading}</div>
        <p className="modal-body">{body}</p>
        <div className="modal-actions">
          <button className="modal-confirm-btn" onClick={onConfirm}>{confirmLabel}</button>
          <button className="modal-cancel-btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  return formatDate(iso, 'full') || iso;
}

function providerLabel(p) {
  if (p === 'lawdrills') return 'Law Drills';
  if (p === 'manual') return 'Manual';
  return 'BARBRI';
}

function ScoreBadge({ pct }) {
  if (pct == null) return <span style={{ color: 'var(--muted)' }}>—</span>;
  const color = pct >= 70 ? 'var(--green)' : pct >= 50 ? 'var(--amber)' : 'var(--red)';
  return <span style={{ fontFamily: 'var(--mono)', fontSize: 17, fontWeight: 600, color }}>{Math.round(pct)}%</span>;
}

function AccuracyBar({ pct }) {
  if (pct == null) return null;
  const p = Math.round(pct);
  const color = p >= 70 ? 'var(--green)' : p >= 50 ? 'var(--amber)' : 'var(--red)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 5, background: 'var(--border2)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: p + '%', height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color, minWidth: 36 }}>{p}%</span>
    </div>
  );
}

const CONF_STYLES = {
  high:   { border: '2px solid var(--green)', bg: '#1a2e1a', label: 'Matched',          color: 'var(--green)' },
  medium: { border: '2px solid var(--amber)', bg: '#2e2510', label: 'Check match',       color: 'var(--amber)' },
  low:    { border: '2px solid var(--amber)', bg: '#2a2408', label: 'Uncertain match',   color: '#b8860b' },
  none:   { border: '2px solid var(--red)',   bg: '#2e1a1a', label: 'No match found',    color: 'var(--red)' },
};

// ── User Guide ────────────────────────────────────────────────────────────────

function UserGuide({ provider }) {
  const [open, setOpen] = useState(false);
  const helpUrl = provider === 'lawdrills' ? '/help/import-law-drills' : '/help/import-barbri';
  return (
    <div className="mcq-guide-panel">
      <button className="mcq-guide-toggle" onClick={() => setOpen(o => !o)}>
        {open ? '▲' : '▼'} How do I copy my results?
      </button>
      {open && (
        <div className="mcq-guide-body">
          {provider === 'barbri' ? (
            <>
              <p>Expand all topic sections in BARBRI, then <kbd>Ctrl+A</kbd> to select the page and <kbd>Ctrl+C</kbd> to copy.</p>
              <ol>
                <li>Open BARBRI and go to your practice question set results.</li>
                <li>Click the ▼ arrow next to <strong>each</strong> topic to expand and show subtopics.</li>
                <li>Press <kbd>Ctrl+A</kbd> (Cmd+A on Mac) to select the entire page.</li>
                <li>Press <kbd>Ctrl+C</kbd> (Cmd+C on Mac) to copy, then paste below.</li>
              </ol>
            </>
          ) : (
            <>
              <p>Select All-time, expand all sections, then <kbd>Ctrl+A</kbd> and <kbd>Ctrl+C</kbd>.</p>
              <ol>
                <li>Open Law Drills and go to your results page.</li>
                <li>Click <strong>All-time</strong> at the top of the page.</li>
                <li>Click the ▶ arrow next to <strong>each</strong> topic to expand subtopics.</li>
                <li>Press <kbd>Ctrl+A</kbd> (Cmd+A on Mac), then <kbd>Ctrl+C</kbd> (Cmd+C on Mac).</li>
              </ol>
            </>
          )}
          <p style={{ marginTop: 8 }}>
            <a href={helpUrl} target="_blank" rel="noreferrer">Full step-by-step guide →</a>
          </p>
        </div>
      )}
    </div>
  );
}

// ── Preview table ─────────────────────────────────────────────────────────────

function ParsedPreview({ results, allTopics, provider, summary, warningMessage, existingImport, onSave, onDiscard }) {
  const [rows, setRows] = useState(() => results.map(r => ({
    ...r,
    include: r.confidence !== 'none',
    user_corrected: false,
  })));

  const topicOptions = [
    { id: null, label: 'Search topics' },
    ...allTopics.map(t => ({
      id: t.id,
      label: `[${t.module}] ${t.subtopic} (${t.topic})`,
    })),
  ];

  const includedCount = rows.filter(r => r.include && r.sqetrack_topic_id).length;

  const handleTopicChange = (idx, newId) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const matched = allTopics.find(t => t.id === newId);
      return { ...r, sqetrack_topic_id: newId, sqetrack_topic_name: matched?.subtopic ?? null, include: !!newId, user_corrected: true };
    }));
  };

  const allMatched = summary.needs_review === 0 && summary.unmatched === 0;

  return (
    <div className="scraper-preview">
      <div className="scraper-preview-title">Review before saving</div>

      <div className="mcq-summary-bar">
        {allMatched
          ? <span>Found <strong>{summary.total}</strong> results, all matched. Check the matches look right, then save.</span>
          : <>
              <span>Found <strong>{summary.total}</strong> results.</span>
              <span style={{ color: 'var(--green)' }}><strong>{summary.auto_matched}</strong> matched.</span>
              {summary.needs_review > 0 && <span style={{ color: 'var(--amber)' }}><strong>{summary.needs_review}</strong> need review.</span>}
              {summary.unmatched > 0 && <span style={{ color: 'var(--red)' }}><strong>{summary.unmatched}</strong> unmatched.</span>}
            </>
        }
      </div>

      {existingImport && provider === 'lawdrills' && (
        <div className="scraper-error" style={{ background: 'rgba(37,99,235,0.08)', borderColor: 'var(--accent)', color: 'var(--accent)', marginBottom: 12 }}>
          You already have a Law Drills import from{' '}
          {formatDate(existingImport.imported_at, 'full')}.{' '}
          Saving this will replace it.
        </div>
      )}

      {warningMessage && (
        <div className="scraper-error" style={{ background: '#2e2510', borderColor: 'var(--amber)', color: 'var(--amber)', marginBottom: 12 }}>
          <Warning size={12} /> {warningMessage}{' '}
          <a href={provider === 'lawdrills' ? '/help/import-law-drills' : '/help/import-barbri'}
            target="_blank" rel="noreferrer" style={{ color: 'var(--amber)' }}>
            See the step-by-step guide →
          </a>
        </div>
      )}

      <p className="mcq-preview-note">Your corrections improve matching for all SQETrack users.</p>

      <div className="scraper-table">
        <div className="reconcile-table-head mcq-preview-head">
          <span>Your result</span>
          <span>SQETrack topic</span>
          <span>Result</span>
          <span>Skip</span>
        </div>
        {rows.map((r, i) => {
          const cs = CONF_STYLES[r.confidence] || CONF_STYLES.none;
          return (
            <div key={i} className="reconcile-table-row mcq-preview-row"
              style={{ borderLeft: cs.border, background: r.include ? cs.bg : 'transparent', opacity: r.include ? 1 : 0.45 }}>
              <div className="scraper-topic-cell">
                <span className="scraper-topic-name">{r.source_label}</span>
                <span style={{ fontSize: 10, color: cs.color, marginTop: 2, display: 'block' }}>{cs.label}</span>
              </div>
              <div>
                <select className="reconcile-topic-select"
                  value={r.sqetrack_topic_id || ''}
                  onChange={e => handleTopicChange(i, e.target.value ? parseInt(e.target.value) : null)}>
                  {topicOptions.map(opt => (
                    <option key={opt.id ?? 'null'} value={opt.id ?? ''}>{opt.label}</option>
                  ))}
                </select>
                {r.user_corrected && (
                  <span style={{ fontSize: 10, color: 'var(--accent)', marginTop: 2, display: 'block' }}>
                    <Check size={9} /> Correction saved
                  </span>
                )}
              </div>
              <ScoreBadge pct={r.score_pct} />
              <input type="checkbox" checked={!r.include} title="Skip"
                onChange={e => setRows(prev => prev.map((x, j) => j === i ? { ...x, include: !e.target.checked } : x))} />
            </div>
          );
        })}
      </div>

      <div className="scraper-actions">
        <button className="scraper-save-btn"
          onClick={() => onSave(rows.filter(r => r.include && r.sqetrack_topic_id && r.score_pct != null))}>
          Save {includedCount} result{includedCount !== 1 ? 's' : ''}
        </button>
        <button className="scraper-discard-btn" onClick={onDiscard}>Cancel</button>
      </div>
    </div>
  );
}

// ── Import history ────────────────────────────────────────────────────────────

function ImportHistory({ refresh: refreshKey, onDeleted, showToast }) {
  const [imports, setImports] = useState(null);
  const [confirm, setConfirm] = useState(null); // { imp } | null
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    api('/api/mcq-imports').then(d => setImports(d.imports || []));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleRemove = (imp) => setConfirm({ imp });

  const handleConfirm = async () => {
    const imp = confirm.imp;
    setConfirm(null);
    setDeleting(imp.batch_id);
    const res = await api(`/api/mcq-imports/${imp.batch_id}`, { method: 'DELETE' });
    setDeleting(null);
    if (res.ok) {
      const n = imp.topic_count;
      showToast(`Import removed. ${n} topic confidence${n !== 1 ? 's' : ''} recalculated.`);
      load();
      onDeleted();
    } else {
      showToast(res.error || 'Could not remove import. Try again.', 'error');
    }
  };

  if (!imports) return <div className="loading">Loading</div>;
  if (imports.length === 0) return <div className="scraper-empty">No imports yet.</div>;

  return (
    <>
      <div className="session-manager">
        <div className="scraper-table-head" style={{ display: 'grid', gridTemplateColumns: '1fr 120px 80px 80px', padding: '8px 12px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <span>Source</span><span>Date</span><span>Topics</span><span></span>
        </div>
        {imports.map(imp => (
          <div key={imp.batch_id} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 80px 80px', padding: '10px 12px', borderTop: '1px solid var(--border)', alignItems: 'center', gap: 8 }}>
            <span className="session-source">{providerLabel(imp.provider)}</span>
            <span className="session-date">{fmtDate(imp.imported_at)}</span>
            <span className="session-topics">{imp.topic_count} topics</span>
            <button className="session-delete-btn" disabled={deleting === imp.batch_id}
              onClick={() => handleRemove(imp)}>
              {deleting === imp.batch_id ? 'Removing' : 'Remove'}
            </button>
          </div>
        ))}
      </div>

      {confirm && (
        <ConfirmModal
          heading="Remove this import?"
          body={`This will remove ${confirm.imp.topic_count} imported result${confirm.imp.topic_count !== 1 ? 's' : ''} and recalculate your topic confidence. This cannot be undone.`}
          confirmLabel="Remove import"
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}

// ── Manual entry ──────────────────────────────────────────────────────────────

function ManualEntry({ allTopics, onSaved, showToast }) {
  const [entries, setEntries] = useState([]);
  const [topicId, setTopicId] = useState('');
  const [score, setScore] = useState('');
  const [saving, setSaving] = useState(false);

  const add = () => {
    const id = parseInt(topicId);
    const pct = parseFloat(score);
    if (!id || isNaN(pct) || pct < 0 || pct > 100) return;
    const topic = allTopics.find(t => t.id === id);
    setEntries(prev => [...prev, { sqetrack_topic_id: id, score_pct: pct, label: topic?.subtopic || '' }]);
    setTopicId(''); setScore('');
  };

  const save = async () => {
    if (!entries.length || saving) return;
    setSaving(true);
    const res = await api('/api/mcq-results/manual', {
      method: 'POST',
      body: JSON.stringify({ results: entries }),
    });
    setSaving(false);
    if (res.ok) {
      showToast(res.message || `Saved ${entries.length} manual ${entries.length === 1 ? 'entry' : 'entries'}.`);
      setEntries([]);
      onSaved();
    } else {
      showToast(res.error || 'Could not save results. Try again.', 'error');
    }
  };

  return (
    <div className="manual-entry-panel">
      <div className="manual-entry-title">Manual entry</div>
      <div style={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 4, marginBottom: 6 }}>
        <label style={{ fontSize: 11, color: 'var(--muted)', paddingTop: 8, gridColumn: '1/-1' }}>Topic</label>
      </div>
      <div className="manual-entry-form">
        <select className="reconcile-topic-select" value={topicId} onChange={e => setTopicId(e.target.value)} style={{ flex: 2 }}>
          <option value="">Search topics</option>
          {allTopics.map(t => (
            <option key={t.id} value={t.id}>[{t.module}] {t.subtopic} ({t.topic})</option>
          ))}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>Result (%)</label>
          <input type="number" min="0" max="100" placeholder="e.g. 75"
            className="manual-score-input" value={score} onChange={e => setScore(e.target.value)} />
        </div>
        <button className="reconcile-select-btn" onClick={add} disabled={!topicId || score === ''}>Add</button>
      </div>

      {entries.length === 0 && (
        <p style={{ fontSize: 12, color: 'var(--hint)', margin: '4px 0 0' }}>
          Select a topic and enter your result above to add entries.
        </p>
      )}

      {entries.length > 0 && (
        <>
          <div className="manual-entries-list">
            {entries.map((e, i) => (
              <div key={i} className="session-row">
                <span className="session-source" style={{ flex: 1 }}>{e.label}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{e.score_pct}%</span>
                <button className="session-delete-btn"
                  onClick={() => setEntries(prev => prev.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
          </div>
          <button className="scraper-save-btn" onClick={save} disabled={saving}>
            {saving ? 'Saving' : `Save ${entries.length} manual ${entries.length === 1 ? 'entry' : 'entries'}`}
          </button>
        </>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MCQScraper() {
  const [provider, setProvider] = useState('barbri');
  const [pastedText, setPastedText] = useState('');
  const [allTopics, setAllTopics] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [parsedSummary, setParsedSummary] = useState(null);
  const [parseWarning, setParseWarning] = useState('');
  const [existingImport, setExistingImport] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [importRefresh, setImportRefresh] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [streak, setStreak] = useState(null);
  const [summary, setSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [savedProvider, setSavedProvider] = useState('barbri');
  const [toastEl, showToast] = useToast();

  const helpUrl = provider === 'lawdrills' ? '/help/import-law-drills' : '/help/import-barbri';

  const refresh = useCallback(() => {
    setImportRefresh(n => n + 1);
    api('/api/mcq-results/summary').then(d => { setSummary(Array.isArray(d) ? d : []); setLoadingSummary(false); });
    api('/api/mcq-streak').then(setStreak);
  }, []);

  useEffect(() => {
    refresh();
    Promise.all([api('/api/topics/FLK1'), api('/api/topics/FLK2')]).then(([a, b]) => {
      setAllTopics([...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])]);
    });
  }, [refresh]);

  const handleParse = async () => {
    if (!pastedText.trim() || parsing) return;
    setParsing(true); setError(''); setParseWarning('');
    try {
      const res = await api('/api/mcq-results/parse', {
        method: 'POST',
        body: JSON.stringify({ text: pastedText, provider }),
      });
      if (res.status === 'error' || res.ok === false) {
        const msg = res.error_message || res.error || 'Parse failed.';
        setError(msg);
      } else {
        setParsed(res.results || []);
        setParsedSummary(res.summary || { total: 0, auto_matched: 0, needs_review: 0, unmatched: 0 });
        setParseWarning(res.error_type === 'partial' ? res.error_message : '');
        setExistingImport(res.existing_import || null);
        if (Array.isArray(res.all_topics) && res.all_topics.length) setAllTopics(res.all_topics);
        setSavedProvider(provider);
      }
    } catch { setError('Parse failed. Try pasting again or check your connection.'); }
    setParsing(false);
  };

  const handleSave = async (results) => {
    if (saving) return;
    setSaving(true);
    const res = await api('/api/mcq-results/save', {
      method: 'POST',
      body: JSON.stringify({ provider: savedProvider, results, raw: pastedText }),
    });
    setSaving(false);
    if (res.error === 'duplicate') {
      const d = res.duplicate_date
        ? formatDate(res.duplicate_date, 'full')
        : 'a previous session';
      showToast(`These results were already imported on ${d}. Remove the existing import first to re-import.`, 'error');
      return;
    }
    if (res.ok !== false) {
      const n = results.length;
      const pName = providerLabel(savedProvider);
      showToast(`Imported ${n} topic confidence${n !== 1 ? 's' : ''} from ${pName}.`);
      trackEvent('mcq_session_imported', { provider: savedProvider, count: n });
      setParsed(null); setPastedText(''); setError(''); setParseWarning(''); setExistingImport(null);
      refresh();
    } else {
      showToast(res.error || 'Could not save results. Try again.', 'error');
    }
  };

  const daysSinceImport = streak?.days_since_import;
  const hasImports = streak && streak.total_imports > 0;
  const importWarning = hasImports && daysSinceImport >= 7;

  return (
    <div className="scraper-page">
      {toastEl}

      <div className="scraper-intro">
        <div className="scraper-intro-title">Import MCQ results</div>
        <p className="scraper-intro-text">Import your practice question scores from BARBRI or Law Drills.</p>
      </div>

      {streak && !hasImports && (
        <div className="streak-bar streak-empty">
          Import your first practice set to calibrate your topic priorities.
        </div>
      )}
      {streak && hasImports && (
        <div className={`streak-bar ${importWarning ? 'streak-warning' : 'streak-ok'}`}>
          {importWarning
            ? <><Warning size={11} /> Last import was {daysSinceImport} days ago. Import your latest results to keep confidence accurate.</>
            : <><Check size={11} /> Last import {daysSinceImport === 0 ? 'today' : `${daysSinceImport} days ago`} · {streak.total_imports} import{streak.total_imports !== 1 ? 's' : ''} logged</>}
        </div>
      )}

      {!parsed && (
        <>
          {/* Provider selector */}
          <div className="mcq-provider-selector">
            <div className={`mcq-provider-card ${provider === 'barbri' ? 'active' : ''}`}
              onClick={() => { setProvider('barbri'); setError(''); }}>
              <div className="mcq-provider-name">BARBRI</div>
              <div className="mcq-provider-sub">Import from a practice set</div>
            </div>
            <div className="mcq-provider-card mcq-provider-disabled">
              <div className="mcq-provider-name">Law Drills</div>
              <div className="mcq-provider-sub">
                Use the <strong>SQETrack Import</strong> Chrome extension for one-click import.{' '}
                <a href="https://chromewebstore.google.com/detail/sqetrack-import/EXTENSION_ID_HERE"
                   target="_blank" rel="noreferrer">Get it here →</a>
              </div>
            </div>
          </div>

          {/* User guide / instructions */}
          <UserGuide provider={provider} />

          {/* Paste area */}
          <div className="scraper-input-section">
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
              Paste your {provider === 'barbri' ? 'BARBRI' : 'Law Drills'} results
            </div>
            <textarea className="scraper-textarea"
              placeholder="Paste your copied results here"
              value={pastedText} onChange={e => setPastedText(e.target.value)} />
            {error && (
              <div className="scraper-error">
                <Warning size={12} /> {error}{' '}
                <a href={helpUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--red)' }}>
                  See the step-by-step guide →
                </a>
              </div>
            )}
            <button className="scraper-parse-btn" onClick={handleParse}
              disabled={parsing || !pastedText.trim()}>
              {parsing ? 'Parsing' : 'Parse results'}
            </button>
          </div>

          {/* Manual entry toggle */}
          <div style={{ marginTop: 8, marginBottom: 4 }}>
            <button className="reconcile-select-btn" style={{ fontSize: 12 }}
              onClick={() => setShowManual(m => !m)}>
              {showManual ? 'Hide manual entry' : "Can't paste your results? Enter them manually."}
            </button>
          </div>
          {showManual && allTopics.length > 0 && (
            <ManualEntry allTopics={allTopics} showToast={showToast}
              onSaved={() => { refresh(); setShowManual(false); }} />
          )}
        </>
      )}

      {parsed && (
        <ParsedPreview
          results={parsed}
          allTopics={allTopics}
          provider={savedProvider}
          summary={parsedSummary}
          warningMessage={parseWarning}
          existingImport={existingImport}
          onSave={handleSave}
          onDiscard={() => { setParsed(null); setParseWarning(''); setExistingImport(null); }}
        />
      )}

      {/* Import history */}
      <div className="scraper-summary-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div className="scraper-summary-title">Import history</div>
          <button className="reconcile-select-btn" onClick={() => setShowHistory(h => !h)}>
            {showHistory ? 'Hide' : 'Manage imports'}
          </button>
        </div>
        {showHistory && (
          <ImportHistory refreshKey={importRefresh} onDeleted={refresh} showToast={showToast} />
        )}
        {!showHistory && (
          <div style={{ fontSize: 12, color: 'var(--hint)' }}>
            {hasImports ? `${streak.total_imports} import${streak.total_imports !== 1 ? 's' : ''} logged.` : 'No imports yet.'}
          </div>
        )}
      </div>

      {/* Per-topic summary */}
      <div className="scraper-summary-section">
        <div className="scraper-summary-title">Per-topic MCQ accuracy</div>
        <div className="blended-explainer">
          Your topic confidence blends your self-rated confidence with MCQ performance. The more MCQs you do on a topic, the more your confidence reflects real performance.
        </div>
        {loadingSummary ? (
          <div className="loading">Loading</div>
        ) : summary.length === 0 ? (
          <div className="scraper-empty">No MCQ results logged yet. Import results above to see per-topic accuracy.</div>
        ) : (
          <div className="scraper-table">
            <div className="scraper-table-head scraper-table-head-wide">
              <span>Topic</span>
              <span>Imports</span>
              <span>MCQ accuracy</span>
              <span>Self-rated</span>
              <span>Blended</span>
              <span>Last import</span>
            </div>
            {summary.map(r => {
              const selfPct = r.self_confidence != null ? Math.round(r.self_confidence * 100) : null;
              const selfColor = selfPct == null ? 'var(--muted)' : selfPct < 50 ? 'var(--red)' : selfPct < 70 ? 'var(--amber)' : 'var(--green)';
              const blended = r.blended_confidence != null ? Math.round(r.blended_confidence * 100) : null;
              const blendedColor = blended == null ? 'var(--muted)' : blended < 50 ? 'var(--red)' : blended < 70 ? 'var(--amber)' : 'var(--green)';
              return (
                <div key={r.topic_id} className="scraper-table-row scraper-table-row-wide">
                  <div>
                    <span className="scraper-topic-name">{r.topic_name || r.subtopic}</span>
                    {r.module && <span className={`scraper-module-tag ${r.module === 'FLK1' ? 'tag-flk1' : 'tag-flk2'}`}>{r.module}</span>}
                  </div>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>{r.sessions}</span>
                  <AccuracyBar pct={r.avg_score_pct} />
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: selfColor }}>
                    {selfPct != null ? selfPct + '%' : '—'}
                  </span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 600, color: blendedColor }}>
                    {blended != null ? blended + '%' : '—'}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>{fmtDate(r.last_session)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
